import { Grid } from '@mui/material/';
import { selectRehabActions } from '@Selectors/Rehab.selectors';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addRehabDeletedAction, deleteRehabActionResolved } from '@Actions';

import {
	Button,
	error,
	MenuListButton,
	openDrawer,
	StylovyzeTable,
	WarningDialog,
} from '@innovyze/stylovyze';
import { StylovyzeHeader } from '@innovyze/lib-am-common';
import { Option, RehabAction } from '@Types';
import { Add, Edit, Delete } from '@mui/icons-material';
import { StyledGrid } from '@Utils/styles';
import { useGlobalization } from '@Translations';
import { useStyles } from '@Components/DecisionTree/DecisionTree.style';
import { useActionTableRows, ActionTableRow } from './useActionTableRows';

export interface RehabActionListProps {
	viewerAccess: boolean;
	isLoading: boolean;
	onRehabActionChange: (rehabAction: RehabAction | null) => void;
	checkActionExists: (rehabAction: RehabAction) => boolean;
	isDirty: boolean;
	isSaveDisabled: boolean;
	isRehabTreeValidating: boolean;
	handleSaveTree: () => void;
	trenchCalculationEnabled?: boolean;
}

// TODO : Replace this with a DATAGRID with local sorting enabled

const RehabActionList = ({
	viewerAccess,
	isLoading,
	onRehabActionChange,
	checkActionExists,
	isDirty,
	isSaveDisabled,
	isRehabTreeValidating,
	handleSaveTree,
	trenchCalculationEnabled = false,
}: RehabActionListProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const rehabActions = selectRehabActions();
	const actionTableRows = useActionTableRows(rehabActions);
	const classes = useStyles();

	const [deleteIndex, setDeleteIndex] = useState(-1);

	const rowMenuItems = (index?: number) => {
		const result = new Array<Option>();
		result.push({
			text: t('Edit'),
			name: 'Edit',
			icon: <Edit />,
			onClick: () => {
				if (
					index != null &&
					index != undefined &&
					rehabActions &&
					rehabActions.length > index &&
					onRehabActionChange
				) {
					onRehabActionChange(rehabActions[index]);
					dispatch(openDrawer('addNewActionDrawer'));
				}
			},
			cy: `${index}-dropdown-edit`,
		});
		result.push({
			text: t('Delete'),
			name: 'Delete',
			icon: <Delete />,
			onClick: () => {
				if (
					index != null &&
					index != undefined &&
					rehabActions &&
					rehabActions.length > index
				) {
					setDeleteIndex(index);
				}
			},
			cy: `${index}-dropdown-delete`,
		});

		return result;
	};

	const menuCell = (index: number) => {
		if (!viewerAccess) {
			return (
				<div
					role="menu"
					tabIndex={0}
					className={isLoading ? 'hidden' : ''}>
					<MenuListButton menuItems={rowMenuItems(index)} />
				</div>
			);
		}
	};

	const headerReactNodes = (): StylovyzeHeader[] => {
		const header = new Array<StylovyzeHeader>();
		header.push(
			{
				key: 'name',
				cell: t('Rehab Action Name'),
				sortable: false,
				align: 'left',
				maxWidth: '200px',
			},
			{
				key: 'costSource',
				cell: t('Cost Source'),
				sortable: false,
				align: 'left',
			},
			{
				key: 'costTable',
				cell: t('Cost Table'),
				sortable: false,
				align: 'left',
			},
			{
				key: 'unitCost',
				cell: t('Unit Cost'),
				sortable: false,
				align: 'left',
			},
			{
				key: 'costPer',
				cell: t('Cost per'),
				sortable: false,
				align: 'left',
			},
			...(trenchCalculationEnabled
				? [
						{
							key: 'costApplication',
							cell: t('Cost Application'),
							sortable: false,
							align: 'left' as const,
						},
					]
				: []),
			{
				key: 'notes',
				cell: t('Notes'),
				sortable: false,
				align: 'left',
			},
		);

		header.push({
			key: 'menu',
			cell: '',
			sortable: false,
			width: '68px',
			align: 'center',
			padding: '0 10px',
		});

		return header;
	};

	const rowReactNodes = (
		row: ActionTableRow,
		index: number,
	): React.ReactNode[] => {
		return [
			row.actionId,
			row.costSource,
			row.costTableName,
			row.unitCostDisplay,
			row.costPerDisplay,
			...(trenchCalculationEnabled ? [row.costApplication] : []),
			row.notes,
			menuCell(index),
		];
	};

	const getTitleArea = () => {
		return (
			<StyledGrid
				alignItems="center"
				justifyContent="flex-end"
				container
				style={{ width: '70%' }}
				spacing={1}>
				{!viewerAccess ? (
					<Grid
						item
						alignItems="center"
						justifyContent="flex-end"
						data-cy="grid-actionbutton"
						wrap="nowrap">
						<Button
							startIcon={<Add />}
							disabled={isLoading}
							variant="contained"
							onClick={() => {
								if (onRehabActionChange)
									onRehabActionChange(null);
								dispatch(openDrawer('addNewActionDrawer'));
							}}>
							{t('Action')}
						</Button>
						<Button
							variant="outlined"
							color="secondary"
							className={classes.treeActionsRightSave}
							onClick={handleSaveTree}
							disabled={isLoading || !isDirty || isSaveDisabled}>
							{isRehabTreeValidating
								? t('Previewing')
								: t('Save')}
						</Button>
					</Grid>
				) : (
					[]
				)}
			</StyledGrid>
		);
	};

	return (
		<>
			<WarningDialog
				open={deleteIndex != -1}
				title={t('Delete Action?')}
				content={t('Are you sure you want to delete Action?')}
				cancelText={t('Cancel')}
				confirmText={t('Delete')}
				onCancel={() => setDeleteIndex(-1)}
				onConfirm={() => {
					if (!checkActionExists(rehabActions[deleteIndex])) {
						dispatch(
							addRehabDeletedAction(
								rehabActions[deleteIndex]._id,
							),
						);

						// Remove it from the actions array
						dispatch(
							deleteRehabActionResolved(
								rehabActions[deleteIndex]._id,
							),
						);

						setDeleteIndex(-1);
					} else {
						dispatch(
							error(
								t(
									'Failed to delete action. Action cannot be deleted if it is being used in tree.',
								),
							),
						);
						setDeleteIndex(-1);
					}
				}}
			/>
			<StylovyzeTable
				className={isLoading ? 'wait' : 'ScrollTable'}
				title={t('Actions')}
				titleArea={getTitleArea()}
				dataCy="actions-table"
				headers={headerReactNodes()}
				rows={
					actionTableRows.map(
						(row: ActionTableRow, nRow: number) => ({
							key: nRow,
							cells: rowReactNodes(row, nRow),
						}),
					) || []
				}
				useSkeleton={{ rowCount: 5 }}
			/>
		</>
	);
};
export default RehabActionList;
